<template>
  <div class="body-post__header">
    <div class="body-post__info">
      <h3
        v-dompurify-html="highlightedTitle"
        class="body-post__title"
        :class="{'body-post__title--bold': isShowFullNews}"
      />
      <div class="body-post__meta">
        <!--    Выбор значимости новости    -->
        <SimpleDropdown
          v-if="newsFeedStore.currentNews.rubric_ids.length > 0"
          width="222px"
          :use-searchbar="false"
          :use-confirm="false"
          :confirm-button-text="t('news_page_body_imp_list.button.save')"
          :title="t('news_page_body_post.importance.title')"
          :is-visible="isShowImportanceDropdown"
          :items="importanceItemList"
          teleport="#app"
          @close="isShowImportanceDropdown = false"
        >
          <template #trigger>
            <AvalChip
              class="body-post__meta--pointer"
              :label="t('news_page_body_post.importance.' +
                (importanceList[newsFeedStore.currentNews?.importance] || importanceList.null).name)"
              :color="(importanceList[newsFeedStore.currentNews?.importance] || importanceList.null).colorName"
              :model-value="newsFeedStore.currentNews?.importance !== null"
              :use-cross-icon="false"
              :use-location-icon="false"
              @click="toggleImportanceDropdown"
            />
          </template>
          <template #dropdown-item>
            <li
              v-for="importance in importanceItemList"
              :key="importance.key"
              class="dropdown__item"
              :class="{'dropdown__item--active':
                parseFloat(importance.key) === parseFloat(newsFeedStore.currentNews?.importance)}"
              @click="setImportance(parseFloat(importance.key))"
            >
              <span
                class="importance-icon"
                :class="['importance-icon--' + importance.colorName]"
              />
              {{ t('news_page_body_post.importance.' + importance.name) }}
            </li>
          </template>
        </SimpleDropdown>

        <!--    Выбор локаций новости    -->
        <AvalDropdown
          v-if="newsFeedStore.currentNews.rubric_ids.length > 0"
          v-model="selectedCitiesList"
          teleport="#app"
          width="222px"
          :empty-message="getCityEmptyMessage"
          max-height="436px"
          :use-confirm="newsFeedStore.projectCities.length > 0"
          :use-search-bar="true"
          :use-show-selected="true"
          :selected-list-max-height="92"
          :items="newsFeedStore.projectCities"
          label-key="city"
          value-key="id"
          :title="t('news_page_body_cities_list.title')"
          :is-visible="isShowLocationDropdown"
          :confirm-button-text="t('news_page_body_cities_list.button.save')"
          @close="isShowLocationDropdown = false"
          @confirm="updateCitiesList"
        >
          <template #trigger>
            <AvalChip
              class="body-post__meta--pointer"
              max-width="180px"
              :use-location-icon="true"
              :label="getCityLabel"
              :use-cross-icon="false"
              @click="toggleLocationDropdown"
            />
          </template>
          <template #title-icon>
            <IconLocation />
          </template>
        </AvalDropdown>
        <span class="body-post__source">{{ newsFeedStore.currentNews?.source_name }}</span>
        <span
          v-if="props.isShowFullNews"
          class="body-post__date"
        >{{ convertTimestampToData(+newsFeedStore.currentNews.date) }}</span>
      </div>
      <!-- Статья попала более чем в одну рубрику — счётчик с тултипом (как в фиде). -->
      <div
        v-if="newsFeedStore.currentNews?.rubrics?.length > 1"
        class="body-post__rubrics"
      >
        <AvalChip
          v-tooltip:bottom="rubricsTooltip"
          :use-cross-icon="false"
          :use-hover="false"
          type="text"
        >
          в {{ newsFeedStore.currentNews.rubrics.length }} рубриках
        </AvalChip>
      </div>
    </div>

    <div class="body-post__menu">
      <!--    Кнопка раскрытия новости в полноэкранном режиме  -->
      <AvalButtonFixedFilled
        v-if="!props.isShowFullNews"
        :icon-only="true"
        @click="emit('toggle-show-full-news')"
      >
        <IconMaximize class="body-post__menu-icon" />
      </AvalButtonFixedFilled>
      <!--    Кнопка для перевода новости   -->
      <AvalButtonFixedFilled
        :key="`translate-${newsFeedStore.currentNews.is_translated}`"
        v-tooltip:bottom-right="getTranslateLabel"
        :icon-only="true"
        :is-active="newsFeedStore.currentNews.is_translated"
        :loading="isActiveTranslateNewsText"
        @click="translateNewsText"
      >
        <IconTranslateFixed
          v-if="newsFeedStore.currentNews.is_translated && !isActiveTranslateNewsText"
        />
        <IconTranslate v-else-if="!newsFeedStore.currentNews.is_translated && !isActiveTranslateNewsText" />
      </AvalButtonFixedFilled>

      <!--    Кнопка для добавления новости в избранное    -->
      <AvalButtonFixedFilled
        v-if="rubricsStore.chosenRubrics.length >= 0 && rubricsStore.isCurrentRubricFixed()"
        v-tooltip:bottom-right="getFavoriteLabel"
        :icon-only="true"
        @click="addToFavorite"
      >
        <SaveIcon
          class="body-post__menu-icon"
          :class="{'body-post__menu-icon--active': newsFeedStore.currentNews.is_favourite}"
        />
      </AvalButtonFixedFilled>
    </div>
  </div>
</template>

<script setup>
import { t } from "@/controllerLK/AvalLocalization";
import { useNewsFeedStore } from "@/store/NewsFeed/newsFeed";
import SaveIcon from "@/components/Icons/SaveIcon.vue";
import {computed, onMounted, ref, defineProps, defineEmits} from "vue";
import {useLK} from "@/controllerLK/LKstore";
import IconTranslateFixed from "@/components/Icons/IconTranslateFixed.vue";
import IconTranslate from "@/components/Icons/IconTranslate.vue";
import {useRubricsStore} from "@/store/NewsFeed/rubrics";
import IconMaximize from "@/components/Icons/IconMaximize.vue";
import {convertTimestampToData} from "@/tools/date-time-tools";
import {highlightHtml} from "@/tools/highlight-html";
import {rubricColor} from "@/tools/rubric-colors";
import {importanceList} from "@/const/importance.list";

const LKStore = useLK();
const newsFeedStore = useNewsFeedStore();
const rubricsStore = useRubricsStore();

const emit = defineEmits("toggle-show-full-news");

const props = defineProps({
  isShowFullNews: {
    type: Boolean,
    default: false
  }
});

// Подсветка слов в заголовке — query оранжевым, matched_terms каждой рубрики — своим цветом.
const highlightedTitle = computed(() => highlightHtml(
    newsFeedStore.currentNews?.title,
    {
      queryWords: newsFeedStore.currentNews?.query_highlights || [],
      rubrics: (newsFeedStore.currentNews?.rubrics || []).map((r) => ({
        name: r.rubric_name,
        terms: r.matched_terms || [],
      })),
    },
));

// Тултип счётчика рубрик — имена подчёркнуты своими цветами (тот же цвет,
// которым подсвечиваются matched_terms этой рубрики в тексте новости).
const rubricsTooltip = computed(() => {
  const list = (newsFeedStore.currentNews?.rubrics || []).filter((r) => r?.rubric_name);
  if (!list.length) return "";
  return list
      .map((r, index) => {
        const color = rubricColor(index);
        const name = escapeTooltipHtml(r.rubric_name);
        return `<span style="text-decoration:underline solid ${color};text-decoration-thickness:2px;text-underline-offset:3px">${name}</span>`;
      })
      .join(", ");
});

function escapeTooltipHtml(str) {
  return String(str ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
}

onMounted(async () => {
  // Устанавливаем дефолтные значения до перевода.
  if (!newsFeedStore.currentNews.is_translated) {
    newsFeedStore.currentNews.is_translated = false;
    newsFeedStore.currentNews.raw_title_default = JSON.parse(JSON.stringify(newsFeedStore.currentNews.title));
    newsFeedStore.currentNews.raw_text_default = JSON.parse(JSON.stringify(newsFeedStore.currentNews.raw_text));
  }

  // Заполняем список выбранных городов в карточке.
  if (newsFeedStore.currentNews.cities.length >= 0) {
    selectedCitiesList.value = newsFeedStore.currentNews.cities.map((item) => item.id);
  }
});

// Добавление новости в избранное
const addToFavorite = () => {
  if (newsFeedStore.currentNews.rubric_ids.length > 0) {
    newsFeedStore.currentNews.is_favourite = !newsFeedStore.currentNews.is_favourite;
    newsFeedStore.setFavorite(newsFeedStore.currentNews.id, newsFeedStore.currentNews.is_favourite);
  }
};

// Получение текста для tooltip для кнопки добавления в избранное.
const getFavoriteLabel = computed(() => {
  if (newsFeedStore.currentNews.is_favourite) {
    return t("news_page_body_post.button.unfavorite");
  } else {
    return t("news_page_body_post.button.favorite");
  }
});

// Получение текста для tooltip кнопки перевода новости.
const getTranslateLabel = computed(() => {
  if (newsFeedStore.currentNews.is_translated) {
    return t("news_page_body_post.button.undo_translate");
  } else {
    return t("news_page_body_post.button.translate");
  }
});

// Флаг загрузки перевода для текста
const isActiveTranslateNewsText = ref(false);

// Функция перевода текста новости
const translateNewsText = async () => {
  if (isActiveTranslateNewsText.value) return false;

  isActiveTranslateNewsText.value = true;
  if (newsFeedStore.currentNews.is_translated) {
    newsFeedStore.currentNews.raw_text = JSON.parse(JSON.stringify(newsFeedStore.currentNews.raw_text_default));
    newsFeedStore.currentNews.title = JSON.parse(JSON.stringify(newsFeedStore.currentNews.raw_title_default));
    newsFeedStore.currentNews.is_translated = false;
  } else {
    const translatedObject = await newsFeedStore.translateNewsText(
        newsFeedStore.currentNews.raw_title_default,
        newsFeedStore.currentNews.raw_text,
        newsFeedStore.currentNews.id
    );
    Object.assign(newsFeedStore.currentNews, translatedObject);
  }
  isActiveTranslateNewsText.value = false;
};

// Локации
// Флаг. Открытие выпадающего списка локаций
const isShowLocationDropdown = ref(false);

// Функция переключения видимости выпадающего списка локаций
const toggleLocationDropdown = () => {
  isShowLocationDropdown.value = !isShowLocationDropdown.value;
};

// Вычисляемая функция получения наименования города, если выбрано > 1
const getCityLabel = computed(() => {
  const cities = newsFeedStore.currentNews?.cities;
  if (cities?.length && cities[0]?.city) {
    return cities[0].city;
  } else {
    return t("news_page_body_cities_list.title");
  }
});

// Вычисляемая функция получения сообщения отсутствия городов.
const getCityEmptyMessage = computed(() => {
  if (newsFeedStore.projectCities.length > 0) {
    return t("news_page_body_post.message.nothing_found");
  } else {
    return t("news_page_body_post.message.empty_cities");
  }
});

// Список выбранных городов для добавления к новости.
const selectedCitiesList = ref([]);

// Функция измения городов карточки.
const updateCitiesList = async () => {
  try {
    await newsFeedStore.setCityCard(newsFeedStore.currentNews.id, selectedCitiesList.value);
    newsFeedStore.currentNews.cities = newsFeedStore.projectCities.filter(city =>
        selectedCitiesList.value.includes(city.id)
    );
  } catch (e) {
    console.log(e);
  } finally {
    isShowLocationDropdown.value = false;
  }
};

const importanceItemList = computed(() =>
    Object.entries(importanceList)
        .filter(([key]) => key !== "null" && key !== "0")
        .map(([key, value]) => ({ ...value, key }))
        .sort((a, b) => parseFloat(a.key) - parseFloat(b.key))
);

// Функция переключения отображения выпадающего списка значимости
// В зависимости от роли пользователя.
const toggleImportanceDropdown = () => {
  if (LKStore.user.roleNames.includes("аналитик")) {
    isShowImportanceDropdown.value = !isShowImportanceDropdown.value;
  }
};

// Флаг открытия выпадающего списка важности
const isShowImportanceDropdown = ref(false);

// Функция изменения значимости новости
const setImportance = async (importance) => {
  try {
    // Ранний возврат если нет текущей новости
    if (!newsFeedStore.currentNews) return;

    const currentImportanceValue = newsFeedStore.currentNews.importance;
    const newImportanceValue = currentImportanceValue === importance ? 0 : importance;

    newsFeedStore.currentNews.importance = newImportanceValue;
    await newsFeedStore.changeImportance(newsFeedStore.currentNews.id, newImportanceValue);
  } catch (error) {
    if (newsFeedStore.currentNews) {
      newsFeedStore.currentNews.importance = newsFeedStore.currentNews.importance === 0 ? importance : 0;
    }
    // TODO: Добавить перевод сообщения в ЛК. Вывести тост.
    console.error("Не удалось изменить значимость", error);
  } finally {
    isShowImportanceDropdown.value = false;
  }
};
</script>

<style lang="scss" scoped>

.importance-icon {
  width: 14px;
  height: 14px;
  background-color: red;
  border-radius: 50%;

  &--green {
    background-color: var(--green-green30);
  }

  &--yellow {
    background-color: var(--yellow-yellow80);
  }

  &--orange {
    background-color: var(--orange-orange80);
  }

  &--red {
    background-color: var(--red-red100);
  }
}

.dropdown {
  &__item {
    display: flex;
    gap: 6px;
    padding: 6px 14px;
    border-radius: 8px;
    cursor: pointer;
    line-height: 18px;
    align-items: center;
    color: var(--text-text-main);

    &--active {
      color: var(--text-text-on);
      &:hover {
        background-color: var(--background-bg-active-hover);
      }
    }

    &:hover:not(&--active) {
      background-color: var(--background-bg-hover);
    }
  }
}

.body-post {
  &__header {
    position: sticky;
    padding: 30px 0 20px 0;
    top: 0;
    background-color: var(--background-bg-white);
    display: flex;
    justify-content: space-between;
    z-index: 1;
    gap: 5px;
  }

  &__source {
    font-weight: 400;
    font-size: 14px;
    line-height: 18px;
    color: var(--text-text-pur);
    align-self: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__date {
    font-weight: 400;
    font-size: 14px;
    line-height: 22px;
    color: var(--text-text-pur);
    white-space: nowrap;
  }

  &__meta {
    display: flex;
    gap: 5px;

    &--pointer {
      white-space: nowrap;
      cursor: pointer;
    }
  }

  &__info {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
  }

  &__title {
    font-weight: 500;
    font-size: 16px;
    line-height: 22px;
    color: var(--text-text-main);

    :deep(*) {
      font-size: 16px;
    }

    &--bold {
      font-size: 20px;

      :deep(*) {
        font-size: 20px;
      }
    }
  }

  &__menu {
    display: flex;
    gap: 6px;

    &-icon {
      width: 17px;
      height: 17px;

      &--active {
        fill: var(--icon-icon-primary);
        color: var(--icon-icon-primary);
      }
    }
  }
}

.body-post__rubrics {
  margin-top: 8px;
}
</style>